import React, { useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { FormattedMessage, useIntl, Link } from 'gatsby-plugin-intl'
import { breakpoint } from 'src/theme/grid'
import HeaderLoggedIn, {
  HeaderGroup
} from 'src/components/header/HeaderLoggedIn'
import { Page } from 'src/components/Wrappers'
import { MENU_ITEMS } from 'src/features/dashboard/duck/consts'
import routes from 'src/utils/routes'
import { Row, SectionBox } from 'src/features/account/components/atoms'
import StockHeader from 'src/features/stocks/components/StockHeader.jsx'
import Slider from 'src/components/Slider'
import NotificationSticky from './components/NotificationSticky'
import connect from './duck/connect'
import { MobileAppStoreBadgeBtn } from 'src/components/static/mobileAppStoreBadgeBtn.jsx'
import { Paper } from '@material-ui/core'
import CustomIcon from 'src/components/CustomIcon'
import { format } from 'date-fns'
import { useQuery } from '@tanstack/react-query'
import NewsService from 'src/services/NewsService'
import MarketplaceService from 'src/services/MarketplaceService'
import { navigate } from 'gatsby-plugin-intl'

const MobileAppsSection = ({ app }) => {
  const intl = useIntl()
  return (
    <MobileAppSectionBox>
      <Title2>
        <FormattedMessage
          id={
            app == 'exchange'
              ? 'common.downloadNewExchangeMobileApp'
              : 'common.downloadNewDriverMobileApp'
          }
        />
      </Title2>
      <MobileStoreWrapper>
        <MobileAppStoreBadgeBtn
          width='120px'
          locale={intl.locale}
          app={app}
          platform='android'
        />
        <MobileAppStoreBadgeBtn
          width='120px'
          locale={intl.locale}
          app={app}
          platform='ios'
        />
      </MobileStoreWrapper>
    </MobileAppSectionBox>
  )
}

const SHOW_MARKETPLACE_AD_LABEL = false

const MarketplaceAdTile = ({
  offer,
  imageSource,
  onClick,
  showLabel = true
}) => (
  <MarketplaceAdCard onClick={onClick}>
    <MarketplaceAdImage src={imageSource} alt={offer.title || 'Marketplace'} />
    {showLabel && (
      <MarketplaceAdLabel>
        <FormattedMessage
          id='dashboard.marketplaceOffer'
          defaultMessage='Marketplace offer'
        />
      </MarketplaceAdLabel>
    )}
  </MarketplaceAdCard>
)

const getMarketplaceImageSource = offer => {
  if (!offer) return null
  if (offer.cardImage?.url) return offer.cardImage.url
  if (offer.logo?.url) return offer.logo.url
  return null
}

const Dashboard = ({
  className,
  getStickyNotificationsList,
  exchangeCounters,
  stickyNotificationsList,
  currentUser
}) => {
  const intl = useIntl()

  const { data: newsResponse, isLoading, isError } = useQuery({
    queryKey: ['news', intl.locale],
    queryFn: async () => {
      const response = await NewsService.getNews()
      return response
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    retry: 2,
    refetchOnWindowFocus: false
  })

  const { data: marketplaceResponse } = useQuery({
    queryKey: ['marketplace-offers-dashboard'],
    queryFn: MarketplaceService.showOffers,
    staleTime: 10 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false
  })

  const marketplaceOffers = useMemo(() => {
    const responseData = marketplaceResponse?.data

    if (Array.isArray(responseData)) {
      return responseData
    }

    if (Array.isArray(responseData?.data)) {
      return responseData.data
    }

    return []
  }, [marketplaceResponse?.data])

  const randomMarketplaceOffer = useMemo(() => {
    if (marketplaceOffers.length === 0) return null

    const startIndex = Math.floor(Math.random() * marketplaceOffers.length)

    for (let offset = 0; offset < marketplaceOffers.length; offset += 1) {
      const offer =
        marketplaceOffers[(startIndex + offset) % marketplaceOffers.length]
      if (getMarketplaceImageSource(offer)) {
        return offer
      }
    }

    return marketplaceOffers[startIndex]
  }, [marketplaceOffers])

  const randomMarketplaceOfferImageSource = getMarketplaceImageSource(
    randomMarketplaceOffer
  )

  const renderMarketplaceAdTile = key => {
    if (!randomMarketplaceOffer || !randomMarketplaceOfferImageSource) {
      return null
    }

    return (
      <MarketplaceAdTile
        key={key}
        offer={randomMarketplaceOffer}
        imageSource={randomMarketplaceOfferImageSource}
        showLabel={SHOW_MARKETPLACE_AD_LABEL}
        onClick={() =>
          navigate(
            `${routes.APP_DASHBOARD}?tab=${MENU_ITEMS.MARKETPLACE}&offerId=${randomMarketplaceOffer.id}`
          )
        }
      />
    )
  }

  useEffect(() => {
    getStickyNotificationsList()
  }, [])

  const formatPublishedDate = dateString => {
    try {
      const cleanDate = dateString.split('.')[0] + 'Z'
      return format(new Date(cleanDate), 'yyyy-MM-dd')
    } catch (error) {
      console.error('Error formatting date:', dateString, error)
      return dateString
    }
  }

  const SliderTitle = (
    <NameWrapper>
      <Title2>
        <FormattedMessage id='dashboard.info' />
      </Title2>
      <Subtitle>
        <FormattedMessage
          values={{ count: stickyNotificationsList.length }}
          id='dashboard.notification'
        />
      </Subtitle>
    </NameWrapper>
  )

  return (
    <Page
      className={className}
      contentPadding={0}
      topBar={
        <HeaderLoggedIn>
          <HeaderGroup>
            <StockHeader>
              {intl.formatMessage({
                id: 'dashboard.title'
              })}
            </StockHeader>
          </HeaderGroup>
        </HeaderLoggedIn>
      }
    >
      <SliderWrapper>
        <NameWrapper>
          <Title>
            <FormattedMessage
              id='dashboard.hello'
              values={{ name: currentUser.firstName }}
            />
          </Title>
          <Subtitle>{currentUser.username}</Subtitle>
        </NameWrapper>
        <Row />
        <Slider
          title={SliderTitle}
          slides={stickyNotificationsList.map((notification, index) => (
            <NotificationSticky
              key={index}
              notification={notification}
              first={index === 0}
              last={index === stickyNotificationsList.length - 1}
            />
          ))}
        />
      </SliderWrapper>
      <NewsSectionBox>
        <Row>
          <NewsSectionHeader>
            <CustomIcon name='announcement' />
            <FormattedMessage
              id='dashboard.newsAndEvents'
              defaultMessage='Nowości i wydarzenia'
            />
          </NewsSectionHeader>
        </Row>
        <NewsEventsContainer elevation={1}>
          <NewsListWrapper>
            {isLoading ? (
              <Subtitle>
                <FormattedMessage id='common.loading' />
              </Subtitle>
            ) : isError ? (
              <>
                {renderMarketplaceAdTile('marketplace-ad-error') || (
                  <Subtitle>
                    <FormattedMessage id='common.loadingError' />
                  </Subtitle>
                )}
              </>
            ) : newsResponse?.data && newsResponse.data.length > 0 ? (
              (() => {
                const marketplaceCard = renderMarketplaceAdTile(
                  'marketplace-ad'
                )
                const items = []
                newsResponse.data.forEach((item, index) => {
                  items.push(
                    <NewsItemWrapper key={item.id}>
                      <NewsContentWrapper>
                        <NewsRow>
                          <NewsTextContent>
                            {item.title && <NewsTitle>{item.title}</NewsTitle>}
                            {item.text && (
                              <NewsContent>{item.text}</NewsContent>
                            )}
                          </NewsTextContent>
                          {item.isNew && (
                            <NewBadge>
                              <FormattedMessage
                                id='common.new'
                                defaultMessage='Nowe'
                              />
                            </NewBadge>
                          )}
                        </NewsRow>
                      </NewsContentWrapper>
                      <NewsFooter>
                        {item.url ? (
                          <NewsLink
                            href={item.url}
                            target='_blank'
                            rel='noopener noreferrer'
                          >
                            <FormattedMessage id={'common.readMore'} />
                          </NewsLink>
                        ) : null}
                        <NewsDate>
                          {formatPublishedDate(item.publishedAt)}
                        </NewsDate>
                      </NewsFooter>
                    </NewsItemWrapper>
                  )
                  if (index === 0 && marketplaceCard) {
                    items.push(marketplaceCard)
                  }
                })
                return items
              })()
            ) : (
              <>
                {renderMarketplaceAdTile('marketplace-ad-only') || (
                  <Subtitle>
                    <FormattedMessage id='common.noNews' />
                  </Subtitle>
                )}
              </>
            )}
          </NewsListWrapper>
        </NewsEventsContainer>
      </NewsSectionBox>
      <MobileAppsSection app='driver' />
      <SectionBox>
        <Row>
          <Title2>
            <FormattedMessage id='dashboard.stocks' />
          </Title2>
        </Row>
        <StocksContainer>
          <Stock>
            <StockTitle>
              <FormattedMessage id='dashboard.exportTitle' />
              <StockOffersCounterMobile>
                {exchangeCounters.export}
              </StockOffersCounterMobile>
            </StockTitle>
            <StockDescription>
              <DescriptionText>
                <FormattedMessage
                  id='dashboard.exportDescription'
                  values={{ b: chunks => <b>{chunks}</b> }}
                />
              </DescriptionText>
              <StockOffersCounter>{exchangeCounters.export}</StockOffersCounter>
            </StockDescription>
            <StockLinks>
              <Link
                to={`${routes.APP_DASHBOARD}?tab=${MENU_ITEMS.STOCKS_CARGO_EXPORT}`}
              >
                <FormattedMessage id='dashboard.stock' />
              </Link>
              <Dot>&#8729;</Dot>
              <Link
                to={`${routes.APP_DASHBOARD}?tab=${MENU_ITEMS.MY_EXPORT_OFFERS}`}
              >
                <FormattedMessage id='dashboard.myOffers' />
              </Link>
              <Dot>&#8729;</Dot>
              <Link
                to={`${routes.APP_DASHBOARD}?tab=${MENU_ITEMS.MY_EXPORT_RESERVATIONS}`}
              >
                <FormattedMessage id='dashboard.myReservations' />
              </Link>
            </StockLinks>
          </Stock>
          <Stock>
            <StockTitle>
              <FormattedMessage id='dashboard.importTitle' />
              <StockOffersCounterMobile>
                {exchangeCounters.import}
              </StockOffersCounterMobile>
            </StockTitle>
            <StockDescription>
              <DescriptionText>
                <FormattedMessage
                  id='dashboard.importDescription'
                  values={{ b: chunks => <b>{chunks}</b> }}
                />
              </DescriptionText>
              <StockOffersCounter>{exchangeCounters.import}</StockOffersCounter>
            </StockDescription>
            <StockLinks>
              <Link
                to={`${routes.APP_DASHBOARD}?tab=${MENU_ITEMS.STOCKS_CARGO_IMPORT}`}
              >
                <FormattedMessage id='dashboard.stock' />
              </Link>
              <Dot>&#8729;</Dot>
              <Link
                to={`${routes.APP_DASHBOARD}?tab=${MENU_ITEMS.MY_IMPORT_OFFERS}`}
              >
                <FormattedMessage id='dashboard.myOffers' />
              </Link>
              <Dot>&#8729;</Dot>
              <Link
                to={`${routes.APP_DASHBOARD}?tab=${MENU_ITEMS.MY_IMPORT_RESERVATIONS}`}
              >
                <FormattedMessage id='dashboard.myReservations' />
              </Link>
            </StockLinks>
          </Stock>
          {process.env.GATSBY_HIDE_GENERAL_CARGO !== 'true' && (
            <Stock>
              <StockTitle>
                <FormattedMessage id='dashboard.generalTitle' />
                <StockOffersCounterMobile>
                  {exchangeCounters.general}
                </StockOffersCounterMobile>
              </StockTitle>
              <StockDescription>
                <DescriptionText>
                  <FormattedMessage
                    id='dashboard.generalDescription'
                    values={{ b: chunks => <b>{chunks}</b> }}
                  />
                </DescriptionText>
                <StockOffersCounter>
                  {exchangeCounters.general}
                </StockOffersCounter>
              </StockDescription>
              <StockLinks>
                <Link
                  to={`${routes.APP_DASHBOARD}?tab=${MENU_ITEMS.STOCKS_GENERAL}`}
                >
                  <FormattedMessage id='dashboard.stock' />
                </Link>
                <Dot>&#8729;</Dot>
                <Link
                  to={`${routes.APP_DASHBOARD}?tab=${MENU_ITEMS.MY_GENERAL_OFFERS}`}
                >
                  <FormattedMessage id='dashboard.myOffers' />
                </Link>
                <Dot>&#8729;</Dot>
                <Link
                  to={`${routes.APP_DASHBOARD}?tab=${MENU_ITEMS.MY_GENERAL_RESERVATIONS}`}
                >
                  <FormattedMessage id='dashboard.myReservations' />
                </Link>
              </StockLinks>
            </Stock>
          )}
          <Stock>
            <StockTitle>
              <FormattedMessage id='dashboard.postImportTitle' />
              <StockOffersCounterMobile>
                {exchangeCounters.post_import_containers}
              </StockOffersCounterMobile>
            </StockTitle>
            <StockDescription>
              <DescriptionText>
                <FormattedMessage
                  id='dashboard.postImportDescription'
                  values={{ b: chunks => <b>{chunks}</b> }}
                />
              </DescriptionText>
              <StockOffersCounter>
                {exchangeCounters.post_import_containers}
              </StockOffersCounter>
            </StockDescription>
            <StockLinks>
              <Link
                to={`${routes.APP_DASHBOARD}?tab=${MENU_ITEMS.STOCKS_POST_IMPORT}`}
              >
                <FormattedMessage id='dashboard.stock' />
              </Link>
              <Dot>&#8729;</Dot>
              <Link
                to={`${routes.APP_DASHBOARD}?tab=${MENU_ITEMS.MY_POST_IMPORT_OFFERS}`}
              >
                <FormattedMessage id='dashboard.myOffers' />
              </Link>
              <Dot>&#8729;</Dot>
              <Link
                to={`${routes.APP_DASHBOARD}?tab=${MENU_ITEMS.MY_POST_IMPORT_RESERVATIONS}`}
              >
                <FormattedMessage id='dashboard.myReservations' />
              </Link>
            </StockLinks>
          </Stock>
          <Stock>
            <StockTitle>
              <FormattedMessage id='dashboard.freeCarriersTitle' />
              <StockOffersCounterMobile>
                {exchangeCounters.free_carriers}
              </StockOffersCounterMobile>
            </StockTitle>
            <StockDescription>
              <DescriptionText>
                <FormattedMessage
                  id='dashboard.freeCarriersDescription'
                  values={{ b: chunks => <b>{chunks}</b> }}
                />
              </DescriptionText>
              <StockOffersCounter>
                {exchangeCounters.free_carriers}
              </StockOffersCounter>
            </StockDescription>
            <StockLinks>
              <Link
                to={`${routes.APP_DASHBOARD}?tab=${MENU_ITEMS.STOCKS_FREE_CARRIERS}`}
              >
                <FormattedMessage id='dashboard.stock' />
              </Link>
              <Dot>&#8729;</Dot>
              <Link
                to={`${routes.APP_DASHBOARD}?tab=${MENU_ITEMS.MY_FREE_CARRIERS_OFFERS}`}
              >
                <FormattedMessage id='dashboard.myOffers' />
              </Link>
              <Dot>&#8729;</Dot>
              <Link
                to={`${routes.APP_DASHBOARD}?tab=${MENU_ITEMS.MY_FREE_CARRIERS_RESERVATIONS}`}
              >
                <FormattedMessage id='dashboard.myReservations' />
              </Link>
            </StockLinks>
          </Stock>
          <Stock>
            <StockTitle>
              <FormattedMessage id='dashboard.contractsTitle' />
              <StockOffersCounterMobile>
                {exchangeCounters.contracts}
              </StockOffersCounterMobile>
            </StockTitle>
            <StockDescription>
              <DescriptionText>
                <FormattedMessage
                  id='dashboard.contractsDescription'
                  values={{ b: chunks => <b>{chunks}</b> }}
                />
              </DescriptionText>
              <StockOffersCounter>
                {exchangeCounters.contracts}
              </StockOffersCounter>
            </StockDescription>
            <StockLinks>
              <Link to={`${routes.APP_DASHBOARD}?tab=${MENU_ITEMS.CONTRACTS}`}>
                <FormattedMessage id='dashboard.stock' />
              </Link>
              <Dot>&#8729;</Dot>
              <Link
                to={`${routes.APP_DASHBOARD}?tab=${MENU_ITEMS.CONTRACTS_OFFERS}`}
              >
                <FormattedMessage id='dashboard.myOffers' />
              </Link>
              <Dot>&#8729;</Dot>
              <Link
                to={`${routes.APP_DASHBOARD}?tab=${MENU_ITEMS.CONTRACTS_RESERVATIONS}`}
              >
                <FormattedMessage id='dashboard.myReservations' />
              </Link>
            </StockLinks>
          </Stock>
        </StocksContainer>
      </SectionBox>
    </Page>
  )
}

const ConnectedComponent = connect(Dashboard)

export default styled(ConnectedComponent)`
  ${SectionBox} {
    padding: 1rem;
    ${breakpoint.m`
      padding: 2rem;
  `}
  }
`
const NewsEventsContainer = styled(Paper)`
  margin-top: 1rem;
  background: transparent;
  box-shadow: none;
  padding: 0;
`

const Title2 = styled.h4`
  display: inline;
  font-size: 0.875rem;
  letter-spacing: 0.1px;
  color: ${({ theme }) => theme.colors.black};
  font-weight: 700;
`

const NewsSectionHeader = styled(Title2)`
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    color: ${({ theme }) => theme.colors.easternBlue.hex()};
  }
`

const NewsListWrapper = styled.div`
  display: grid;
  margin-top: 0.75rem;
  gap: 0 1rem;

  ${breakpoint.m`
    grid-template-columns: repeat(1, 1fr);
  `}
  ${breakpoint.l`
    grid-template-columns: repeat(2, 1fr);
  `}
  ${breakpoint.xl`
    grid-template-columns: repeat(3, 1fr);
    `}
`

const NewsItemWrapper = styled.div`
  position: relative;
  padding: 0.75rem 1.5rem;
  background: ${({ theme }) => theme.colors.white.alpha(0.9).hex()};
  border-bottom: 2px solid ${({ theme }) => theme.colors.ghost.alpha(0.3).hex()};
  display: flex;
  flex-direction: column;
`

const NewsContentWrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 8px;
`

const NewsTextContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const NewBadge = styled.span`
  display: inline-block;
  background: ${({ theme }) => theme.colors.easternBlue.hex()};
  color: ${({ theme }) => theme.colors.white.hex()};
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: bold;
  margin-right: 8px;
  align-self: flex-start;
`

const NewsDate = styled.span`
  display: block;
  color: ${({ theme }) => theme.colors.ebonyClay.hex()};
  font-size: 0.75rem;
  margin-top: 8px; // Replace theme.spacing(1)
  // move to the bottom
  align-self: flex-end;
  // move to the lfet
  text-align: right;
  margin-left: auto;
`

const NewsLink = styled.a`
  color: ${({ theme }) => theme.colors.easternBlue.hex()};
  text-decoration: none;
  font-size: 0.75rem;
  &:hover {
    text-decoration: underline;
  }
`

const NewsFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 8px;
`

const NewsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`

const SliderWrapper = styled.div`
  width: 100%;
  backdrop-filter: blur(5px);
  background-color: ${({ theme }) => theme.colors.lightGrey.hex()};
  border-bottom-left-radius: 50% 20%;
  border-bottom-right-radius: 50% 20%;
  padding: 1rem 0;
  margin-bottom: 10px;
  box-sizing: border-box;
  ${Row} {
    align-items: baseline;
  }
  ${breakpoint.m`
    max-height: 310px;
    padding: 2rem 0;
  `}
`

const Title = styled.h3`
  display: inline;
  font-size: 1.125rem;
  line-height: 1.78;
  color: ${({ theme }) => theme.colors.black};
  font-weight: 700;
`

export const Subtitle = styled.span`
  margin-left: 0.5rem;
  font-size: 0.75rem;
  line-height: 2.67;
  color: ${({ theme }) => theme.colors.black};
  font-weight: 400;
  opacity: 0.7;
`

const StocksContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const Stock = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 96px;
  background-color: ${({ theme }) => theme.colors.white.alpha(0.9).hex()};
  border-bottom: 2px solid ${({ theme }) => theme.colors.ghost.alpha(0.3).hex()};
  padding: 0.75rem 1.5rem;
  &:first-of-type {
    margin-top: 0.75rem;
  }
  ${breakpoint.m`
    position: static;
  `}
`
const StockTitle = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 1rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.black.hex()};
  font-weight: 700;
`
const StockDescription = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

export const DescriptionText = styled.div`
  font-size: 0.875rem;
  line-height: 1.71;
  color: ${({ theme }) => theme.colors.black.hex()};
  font-weight: 400;
`

const NewsContent = styled.div`
  font-size: 0.875rem;
  line-height: 1.71;
  color: ${({ theme }) => theme.colors.black.hex()};
  font-weight: 400;
`

const StockOffersCounter = styled.div`
  background-color: ${({ theme }) => theme.colors.lightGrey.hex()};
  border-radius: 16px;
  padding: 0rem 0.5rem;
  color: ${({ theme }) => theme.colors.apple.hex()};
  font-size: 0.75rem;
  line-height: 2;
  font-weight: 700;
  display: none;
  ${breakpoint.m`
    display: block;
    padding: 0.3125rem 1rem;
  `}
`

const StockOffersCounterMobile = styled.div`
  background-color: ${({ theme }) => theme.colors.lightGrey.hex()};
  border-radius: 16px;
  padding: 0rem 0.5rem;
  color: ${({ theme }) => theme.colors.apple.hex()};
  font-size: 0.75rem;
  line-height: 2;
  font-weight: 700;
  display: block;
  ${breakpoint.m`
    display: none;
  `}
`

const StockLinks = styled.div`
  margin-top: 0.3rem;
  color: ${({ theme }) => theme.colors.ebonyClay.hex()};
  font-size: 0.75rem;
  font-weight: 400;
  a {
    color: ${({ theme }) => theme.colors.easternBlue.hex()};
  }
`

const Dot = styled.span`
  margin: 0 1rem;
`

const NameWrapper = styled(Row)`
  box-sizing: border-box;
  padding: 0 1rem;
  ${breakpoint.m`
    padding: 0 2rem;
  `}
`
const MobileStoreWrapper = styled.div`
  display: flex;
  height: auto;
  gap: 0.5rem;
  flex-direction: row;
  align-items: center;
  padding-top: 0.25rem;
`

const MobileAppSectionBox = styled(SectionBox)`
  padding-top: 0.5rem !important;
  padding-bottom: 0.1rem !important;
  margin: 0;
  height: auto;
`

export const Container = styled.div`
  min-width: 400px;
  padding: 1.5rem 1rem 0rem 1rem;
  & > div {
    margin: 0.7rem 0;
  }

  ${breakpoint.m`
        padding: 1.5rem 2rem 0rem 2rem;
    `}
`

export const DialogContent = styled.div`
  color: ${({ theme }) => theme.colors.black};
  font-weight: 400;
  font-size: 0.875rem;
  line-height: 1.71;

  & ul {
    list-style-type: disc;
    padding-left: 2rem;
  }
  & ol {
    padding-left: 2rem;
  }
  & a {
    color: ${({ theme }) => theme.colors.easternBlue.hex()};
    &:hover {
      color: ${({ theme }) => theme.colors.easternBlue.darken().hex()};
    }
  }
`

export const DialogSubtitle = styled.h5`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.black};
  font-weight: 600;
`

export const TermsPreview = styled(DialogContent)`
  overflow-y: auto;
  box-sizing: border-box;
  max-height: 300px;
  background-color: #f8f8f9;
  border-radius: 4px;
  padding: 0.5rem;
`

const NewsSectionBox = styled(SectionBox)`
  padding-top: 0.1rem !important;
  padding-bottom: 0.1rem !important;
`

const MarketplaceAdCard = styled.div`
  position: relative;
  cursor: pointer;
  overflow: hidden;
  border-bottom: 2px solid ${({ theme }) => theme.colors.ghost.alpha(0.3).hex()};
  transition: box-shadow 0.3s ease;

  &:hover {
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
    border-left: 3px solid ${({ theme }) => theme.colors.deYork.hex()};
  }
`

const MarketplaceAdImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  max-height: 200px;
`

const MarketplaceAdLabel = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  background-color: ${({ theme }) => theme.colors.apple.hex()};
  color: white;
  padding: 0.25rem 0.65rem;
  font-size: 0.75rem;
  font-weight: 700;
  pointer-events: none;
`

const NewsTitle = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.black.hex()};
`
